<template>
	<div class="org-permissions-main">
		<div class="permissions-header">
			<h1 v-t="'admin_overview_roles_permissions_title'" />

			<Button
				primary
				class="save-button"
				@click="save()"
				:disabled="!changed"
				:loading="isUpdating"
			>
				{{
					isUpdating ? $t('meetings_record_saving') : $t('common_button_save')
				}}
			</Button>
		</div>

		<div class="permissions-content">
			<div
				v-if="isUpdating"
				class="permissions-content-updating"
				:class="{
					'permissions-content-updating-banner': store.showAnnouncementsBanner,
				}"
			></div>

			<div v-if="!showLoading" class="permissions-help">
				<p>
					{{ $t('admin_permissions_desc') }}
					<a
						class="pointer"
						href="https://support.pronto.io/articles/4148273"
						target="_blank"
						>{{ $t('admin_permissions_desc_link') }}
					</a>
				</p>
			</div>

			<div v-if="showLoading" class="org-permissions-loading">
				<div style="font-family: Avenir-Medium; color: #8a8d93;">
					{{ $t('common_loader_loading_title') }}
				</div>
				<div id="mainSpinner" class="ios-spinner">
					<div class="bar1"></div>
					<div class="bar2"></div>
					<div class="bar3"></div>
					<div class="bar4"></div>
					<div class="bar5"></div>
					<div class="bar6"></div>
					<div class="bar7"></div>
					<div class="bar8"></div>
					<div class="bar9"></div>
					<div class="bar10"></div>
					<div class="bar11"></div>
					<div class="bar12"></div>
				</div>
			</div>

			<div v-if="!showLoading" class="main-table-wrapper">
				<BasicTable :columns="columns" :rows="permissions">
					<template v-slot:cell-description="{ row: permission }">
						<div class="permission-desc">{{ permission.description }}</div>
						<div v-if="!permission.disabled" class="permission-detail">
							{{ permission.detail }}
						</div>

						<div v-if="permission.disabled" class="permission-disabled-text">
							{{ $t('admin_permissions_disabled') }}
							<a
								href="https://support.pronto.io/articles/4148279"
								target="_blank"
								>{{ $t('admin_permissions_disabled_link') }}</a
							>
						</div>
					</template>

					<template v-slot:cell-admin="{ row: permission }">
						<img
							:class="{ hidden: permission.disabled }"
							src="/images/common/blue-checkmark.svg"
						/>
					</template>

					<template v-slot:cell-manager="{ row: permission }">
						<Checkbox
							v-model="permission.manager"
							@change="managerChecked(permission)"
							:class="{ hidden: permission.disabled }"
						/>
					</template>

					<template v-slot:cell-user="{ row: permission }">
						<Checkbox
							v-model="permission.user"
							@change="userChecked(permission)"
							:class="{ hidden: permission.disabled }"
						/>
					</template>
				</BasicTable>
			</div>
		</div>
	</div>
</template>

<script>
import store, { getOrganization } from '@/store'
import { Button, Checkbox } from '@/components/common'
import BasicTable from '../../BasicTable.vue'
import { updateOrg } from '@/api/organization'
import { getErrorText } from '@/helperMethods'

export default {
	components: { BasicTable, Checkbox, Button },
	data() {
		return {
			store,
			permissions: [
				{
					id: 'create_group',
					description: this.$t('admin_permissions_create_group'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_create_group_desc'),
				},
				{
					id: 'search_org',
					description: this.$t('admin_permissions_search_org'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_search_org_desc'),
				},
				{
					id: 'create_announcement',
					description: this.$t('admin_permissions_org_announcements'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_org_announcements_desc'),
				},
				{
					id: 'create_dm',
					description: this.$t('admin_permissions_send_dm'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_send_dm_desc'),
				},
				{
					id: 'create_multidm',
					description: this.$t('admin_permissions_create_multi_dm'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_create_multi_dm_desc'),
				},
				{
					id: 'add_user',
					description: this.$t('admin_permissions_add_user'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_add_user_desc'),
				},
				{
					id: 'take_screenshot',
					description: this.$t('admin_permissions_take_screenshot'),
					admin: true,
					manager: false,
					user: false,
					visible: true,
					disabled: false,
					detail: this.$t('admin_permissions_take_screenshot_desc'),
				},
			],
			isUpdating: false,
			showLoading: true,
			org: getOrganization() || {},
			changed: false,
		}
	},

	computed: {
		columns() {
			return {
				description: this.$t('admin_permissions_permission_title'),
				admin: this.$t('admin_permissions_admin_role'),
				manager: this.$t('admin_permissions_admin_manager_role'),
				user: this.$t('admin_permissions_admin_user_role'),
			}
		},
	},

	mounted() {
		this.$onEventBus('user-update', () => {
			this.org = getOrganization()
			this.loadPermissions()
		})

		this.loadPermissions()
	},

	methods: {
		managerChecked(permission) {
			const position = this.permissions.findIndex((x) => x.id === permission.id)
			// if user is checked EVERYTHING is checked
			if (position !== -1 && permission.manager) {
				this.permissions[position].manager = true
			} else if (position !== -1 && !permission.manager) {
				this.permissions[position].user = false
			}

			this.handleDmPermissionRelationship(permission)
			this.changed = true
		},

		userChecked(permission) {
			const position = this.permissions.findIndex((x) => x.id === permission.id)
			// if user is checked EVERYTHING is checked
			if (position !== -1 && permission.user) {
				this.permissions[position].manager = true
			}

			this.handleDmPermissionRelationship(permission)
			this.changed = true
		},

		handleDmPermissionRelationship(permission) {
			// Handle create_multidm checking create_dm if it's completely unchecked
			if (permission.id === 'create_multidm') {
				const createDmPerm = this.permissions.find((x) => x.id === 'create_dm')
				if (createDmPerm && !createDmPerm.manager && !createDmPerm.user) {
					createDmPerm.manager = permission.manager
					createDmPerm.user = permission.user
				}
			}

			// If create_dm is unchecked, clear corresponding level in create_multidm
			if (permission.id === 'create_dm') {
				const createMultiDmPerm = this.permissions.find(
					(x) => x.id === 'create_multidm'
				)
				if (createMultiDmPerm) {
					if (!permission.user) {
						createMultiDmPerm.user = false
					}
					if (!permission.manager && !permission.user) {
						createMultiDmPerm.manager = false
					}
				}
			}
		},

		loadPermissions() {
			if (this.org.id == null) return

			this.permissions.forEach((perm) => {
				let role = this.org[perm.id]
				if (role === 'user') {
					perm.user = true
					perm.manager = true
				} else if (role === 'manager') {
					perm.manager = true
				}

				if (
					perm.id === 'create_announcement' &&
					!this.org.announcements_enabled
				) {
					perm.visible = false
				}

				if (!this.org['grant_' + perm.id]) {
					perm.disabled = true
				}
			})
			this.showLoading = false
		},

		async save() {
			this.isUpdating = true

			const body = this.permissions
				.filter((p) => p.visible)
				.reduce((data, perm) => {
					if (perm.user) {
						data[perm.id] = 'user'
					} else if (perm.manager) {
						data[perm.id] = 'manager'
					} else {
						data[perm.id] = 'admin'
					}
					return data
				}, {})

			try {
				await updateOrg(body)
				this.$alert.success(
					this.$t('admin_people_changes_saved_confirmation_label')
				)
			} catch (e) {
				console.error(e)
				this.error = true
				this.$alert.error(this.$t(getErrorText(e.response.data.error)))
			}

			this.isUpdating = false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.save-button {
	position: absolute;
	top: 32px;
	right: 32px;
}

.permissions-header {
	display: flex;
	align-items: center;
	height: 105px;
	padding-left: 32px;
	position: relative;
	h1 {
		font-family: Avenir-Heavy;
		font-size: 36px;
		margin: 0;
		color: var(--neutral-800);
	}
	border-bottom: 1px solid var(--neutral-200);
}

.permissions-content {
	padding-top: 16px;
	padding-bottom: 50px;
	height: calc(100vh - 60px);
	overflow-y: scroll;
}

.back-btn-bg {
	font-family: 'Avenir-Heavy';
	font-size: 36px;
	color: #4f5053;
	padding-top: 5px;
	margin-left: 14px;
}

.main-table-wrapper {
	justify-content: center;
	align-items: center;
	padding-left: 40px;
	padding-right: 40px;
}

.permissions-content-updating {
	background-color: rgba(0, 0, 0, 0.25);
	position: absolute;
	z-index: 1;
	top: 104px;
	left: 64px;
	right: 0;
	bottom: 0;
	pointer-events: none;
}

.permissions-content-updating-banner {
	top: 190px;
}

.permission-desc {
	color: #4f5053;
	font-family: 'Avenir-Heavy';
	font-size: 15px;
}

.permission-detail,
.permission-disabled-text {
	color: #8e8e8e;
	font-size: 13px;
	font-family: 'Avenir-Medium';
	margin-top: 4px;
}

.permission-disabled-text a {
	text-decoration: none;
	color: var(--blue-300);
}

.permissions-help {
	color: #9498a3;
	font-family: 'Avenir-Medium';
	font-size: 14px;
	padding-right: 40px;
	padding-left: 40px;
	margin-bottom: 16px;
}

.permissions-help a {
	text-decoration: none;
	color: var(--blue-300);
	font-family: 'Avenir-Heavy';
}

.permissions-help:hover #info-text {
	color: var(--blue-300);
}
.permissions-help:hover #info-svg #info-icon {
	fill: var(--blue-300);
}

#info-svg {
	vertical-align: middle;
}

table {
	border-collapse: separate;
	border-spacing: 0;

	:deep(th) {
		background-color: #fafafa;
		height: 77px;
		font-family: 'Avenir-Roman';
		font-size: 15px;
		color: #2c2d30;
		font-weight: normal;
		font-stretch: normal;
		font-style: normal;
		line-height: normal;
		text-align: center;
		border-top: 1px solid #e2e4e6;
		padding-left: 20px;
		padding-right: 20px;
	}

	:deep(th:first-child) {
		text-align: left;
		padding-left: 25px;
		border-top-left-radius: 4px;
	}

	:deep(th:last-child) {
		border-top-right-radius: 4px;
	}

	:deep(th:first-child),
	:deep(tr) td:first-child {
		border-left: 1px solid #e2e4e6;
	}

	:deep(tr) td:first-child {
		font-family: 'Avenir-Medium';
		font-size: 14px;
		font-weight: 500;
		font-stretch: normal;
		font-style: normal;
		line-height: normal;
		letter-spacing: normal;
		color: #8e8e8e;
	}

	:deep(th:last-child),
	:deep(tr) td:last-child {
		border-right: 1px solid #e2e4e6;
	}

	:deep(tr:last-child) td:first-child {
		border-bottom-left-radius: 4px;
	}

	:deep(tr:last-child) td:last-child {
		border-bottom-right-radius: 4px;
	}

	:deep(tbody) tr {
		height: 67px;
	}

	:deep(tr) td:nth-child(even) {
		background: #fafafa;
	}

	:deep(tr) td {
		text-align: center;
		border-bottom: 1px solid #e2e4e6;
	}

	:deep(tr) td:nth-child(4n + 1) {
		text-align: left;
		padding-left: 25px;
	}
}

:deep(.m-chckbox--container) {
	margin-right: 0;
	width: 18px;
}

:deep(.m-chckbox--group) {
	border: 1px solid #9498a3;
}

:deep(.m-chckbox--label) {
	padding-left: 0;
}

:deep(.m-chckbox--ripple) {
	transform: none;
}

:deep(.__ripple__container) {
	display: none;
}

@media screen and (max-width: 800px) {
	.org-permissions-main {
		width: 100vw;
	}

	.permissions-header {
		padding-left: 68px;
	}
}

.org-permissions-loading {
	text-align: center;
	height: calc(90vh - 203px);
	padding-top: calc(50vh - 308px);
}
</style>
